import * as _ from "lodash"
import {
    AttributeNumberType,
    AttributeType,
    IdsAttribute,
    IdsMetric,
    IdsRow,
    IMetricDataSet
} from "./PA.MetricDataSet.IF";
import { FilterCondition, FilterUtils } from "../PA.Core/Basic/Filter";
import { EvalFunction, parse } from "mathjs";
import { DataSetUtils } from "./DataSetUtils";
import { MessageProvider } from "../PA.Core/Basic/Messages";


export interface IComputedAttributeCreateOptions {
    // Required for calculation order
    requiredMetricsIds?: string[]
    requiredAttributeIds?: string[]

    // For Condition Driven Computed Attribute
    conditionValueSet?: {
        condition?: FilterCondition,
        formula?: string,
        value: any
    }[]
    unresolvedValue?: any // Value when Metrics or Attributes not available in dataset
    defaultValue?: any

    // For Completely Manual Computed Attribute
    getComputedValue?: (dataSet: IMetricDataSet, row: IdsRow) => any
    setComputedValuesAllRows?: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  allRows: IdsRow[])=> Promise<void>
    setComputedValuesFilteredRows?: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[], filteredRows: IdsRow[], allRows: IdsRow[])=> Promise<void>
}

export class ComputedAttribute
    implements IdsAttribute
{
    private _options: IComputedAttributeCreateOptions
    private _Id: string
    private _Name: string
    private _ShortName: string
    private _Type: AttributeType
    private _SortOrder: number
    private _numberFormat: string // msFormat "#,##0.00"
    private _storeKey: string
    private _createMessageProvider: MessageProvider
    private _getComputedValue: (dataSet: IMetricDataSet, row: IdsRow) => any
    private _setComputedValuesAllRows: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  allRows: IdsRow[]) => Promise<void>
    private _setComputedValuesFilteredRows: (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  filteredRows: IdsRow[], allRows: IdsRow[]) => Promise<void>
    private _requiredMetricsIds: string[]
    private _requiredAttributeIds: string[]


    constructor(settings: IdsAttribute, options: IComputedAttributeCreateOptions){
            this._options = options || {};
            this._Id = settings.Id
            this._Name = settings.Name
            this._ShortName = settings.ShortName
            this._Type = settings.Type
            this._SortOrder = settings.SortOrder
            this._numberFormat = settings.numberFormat
            this._storeKey = `computed${this._Id}`
            this._createMessageProvider = new MessageProvider()

           // inspect options and set computed attribute properties
          this.setupComputedAttribute()
    }

    get Id(): string { return this._Id }
    get Name(): string { return this._Name }
    get ShortName(): string { return this._ShortName }
    get SortOrder(): number { return this._SortOrder }
    get Type(): AttributeType { return this._Type }
    get numberFormat(): string { return this._numberFormat; }

    get bAttributeComputed(): boolean { return true}
    getComputedValue(dataSet: IMetricDataSet, row: IdsRow): any {
        return this._getComputedValue(dataSet, row);
    }

    get setComputedValuesAllRows(): (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  rows: IdsRow[]) => Promise<void> {
        return this._setComputedValuesAllRows
    }
    get setComputedValuesFilteredRows(): (dataSet: IMetricDataSet , rowAttributeIds: string[], rowMetricIds: string[],  filteredRows: IdsRow[], allRows: IdsRow[]) => Promise<void> {
        return this._setComputedValuesFilteredRows
    }

    get requiredAttributeIds(): string[] { return this._requiredAttributeIds }
    get requiredMetricsIds(): string[] { return this._requiredMetricsIds }


    private setupComputedAttribute(): void {
        let opts = this._options
        if(_.isArray(opts.conditionValueSet)
           && !_.isEmpty(opts.conditionValueSet)){
            this.setupConditionsComputedAttribute()
            return
        }

        if(_.isFunction(opts.getComputedValue)) {
            this.setupManualComputedAttribute()
            return
        }

        this.setupEmptyComputedAttribute()
    }

    private setupConditionsComputedAttribute(): void {
        const self = this
        const opts = this._options

        self._requiredMetricsIds = opts.requiredMetricsIds || [];
        self._requiredAttributeIds = opts.requiredAttributeIds || [];

        const dctAttributeTypeSimpleType = {
            "string":  "string",
            "number":  "number",
            "node":    "number",
            "date":    "date",
            "sapdate": "string"
        }

        // Create Conditional Function
        self._getComputedValue = (dataSet: IMetricDataSet, row: IdsRow) => {
            if (_.isEmpty(dataSet))
                return undefined;
            return dataSet.getRowData(row, self._storeKey);
        };

        // Check if there are any references to Visual Functions,
        //   if so, apply to filter scope
        let bApplyToFilterScope = false
        let dctS: any = {}
        let dctFnc: any = {}
        let sError: string[] = []
        _.forEach(opts.conditionValueSet, (item) => {
            if(_.isString(item.formula) && !_.isEmpty(item.formula)) {
                try {
                    let p = parse(item.formula)
                    DataSetUtils.Formula_setDependentParts(p, dctS, dctFnc, sError)
                } catch (err) {
                    //--
                    sError.push(`Unable to parse formula ${item.formula}`)
                }
            }
        })
        _.forEach(_.values(dctFnc), (fncName) => {
            let spc = DataSetUtils.Function_getSpecs(fncName)
            if (spc && spc.bFilterScope) {
                bApplyToFilterScope = true
            }
        })

        // Add any symbol references to requiredMetrics
        _.forEach(_.values(dctS), (s) => {
            if(_.includes(self._requiredAttributeIds, s) ||
                _.includes(self._requiredMetricsIds, s))
                return
            // Assume it's a metricId
            self._requiredMetricsIds.push(s)
        })


        self._setComputedValuesFilteredRows = undefined;
        self._setComputedValuesAllRows = undefined;

        async function ComputeValues(dataSet: IMetricDataSet,
                                     rowAttributeIds: string[],
                                     rowMetricIds: string[],
                                     filterRows: IdsRow[],
                                     allRows: IdsRow[]): Promise<void>
        {

            if (_.isEmpty(dataSet))
                return;

            let missingAttr = _.difference(self.requiredAttributeIds, rowAttributeIds);
            let missingMtrc = _.difference(self.requiredMetricsIds, rowMetricIds);
            if (missingAttr.length || missingMtrc.length) {
                // Set to default value ?
                if (!_.isUndefined(opts.unresolvedValue)) {
                    _.forEach(allRows, (row) => {
                        dataSet.setRowData(row, self._storeKey, opts.unresolvedValue);
                    });
                }
                return;
            }

            const dctAttr: { [attrId: string]: IdsAttribute } = {};
            const dctCompAttr: { [attrId: string]: IdsAttribute } = {};
            const dctMtrc: { [metricId: string]: IdsMetric } = {};
            const dctSimpleType: { [symbolId: string]: "number" | "string" | "date" } = {};
            let expFuncCount = 0
            _.forEach(rowAttributeIds, (attrId) => {
                const atr = dataSet.getAttribute(attrId);
                if (atr.bAttributeComputed)
                    dctCompAttr[atr.Id] = atr;
                else
                    dctAttr[atr.Id] = atr;
                dctSimpleType[atr.Id] = <any>(dctAttributeTypeSimpleType[atr.Type]);
            });
            _.forEach(rowMetricIds, (metricId) => {
                const mtrc = dataSet.getMetric(metricId);
                dctMtrc[mtrc.Id] = mtrc;
                dctSimpleType[mtrc.Id] = "number";
            });


            function symbolGet(symbolId: string, row: IdsRow): any {
                if (symbolId in dctAttr)
                    dataSet.getRowAttribute(row, symbolId);
                if (symbolId in dctCompAttr) {
                    const atr = dataSet.getAttribute(symbolId);
                    return atr.getComputedValue(dataSet, row);
                }
                if (symbolId in dctMtrc)
                    return dataSet.getRowMetric(row, symbolId);
            }


            let caseFunctions: {
                condition?: FilterCondition,
                cndFunc?: (row: IdsRow) => boolean,
                expFormula?: string,
                expFunc?: EvalFunction,
                value: any
            }[] = [];

            // Create Condition Functions
            _.forEach(opts.conditionValueSet, (item) => {
                let val = item.value;

                // Either use Condition or Formula Expression
                if(!_.isUndefined(item.condition)) {
                    let cnd = item.condition;
                    let fnCnd = FilterUtils.createFunction(cnd, dctSimpleType, symbolGet);

                    if (!_.isFunction(fnCnd)) {
                        console.log("Unable to compile Condition");
                        return;
                    }

                    caseFunctions.push({
                        condition: cnd,
                        cndFunc: fnCnd,
                        value: val
                    });
                } else if(_.isString(item.formula) && !_.isEmpty(item.formula)) {
                    try {
                        let p = parse(item.formula)
                        let frmFunc = p.compile()
                        expFuncCount++
                        caseFunctions.push({
                            expFormula: item.formula,
                            expFunc: frmFunc,
                            value: val
                        })
                    } catch (err) {
                        //--
                    }

                }


            });

            let applyRows = allRows
            if(bApplyToFilterScope)
                applyRows = filterRows

            let calcCtx: {row: IdsRow, [id:string]: any} = {row: undefined}

            let fncScope: any = {}
            if(expFuncCount){
                fncScope.VisualAvg = (metricId: string, wghtMetricId: string, bPositiveOnly?: boolean): number => {
                    let fncKey = `VizAvg-${metricId || '-'}-${wghtMetricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc avg and set key
                    let fopts: any = {positiveOnly: !!bPositiveOnly};
                    if(!_.isEmpty(wghtMetricId)){
                        fopts.weightByMetricId = wghtMetricId
                        fopts.divZeroDefault = 0
                        fopts.fallBackToNoWeight = true
                    }
                    DataSetUtils.Metric_setAvg(dataSet, metricId, fncKey, applyRows, fopts)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualMin = (metricId: string, bPositiveOnly?: boolean): number => {
                    let fncKey = `VizMin-${metricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc min and set key
                    let fopts: any = {bPositiveOnly: !!bPositiveOnly};
                    DataSetUtils.Metric_setMin(dataSet, metricId, fncKey, applyRows, fopts)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualMax = (metricId: string, bPositiveOnly?: boolean): number => {
                    let fncKey = `VizMax-${metricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc max and set key
                    let fopts: any = {bPositiveOnly: !!bPositiveOnly};
                    DataSetUtils.Metric_setMax(dataSet, metricId, fncKey, applyRows, fopts)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }
                fncScope.VisualAgg = (metricId: string): number => {
                    let fncKey = `VizAgg-${metricId || '-'}`;
                    if(fncKey in calcCtx)
                        return dataSet.getRowData(calcCtx.row, fncKey);
                    // calc agg and set key
                    DataSetUtils.Metric_setAggregate(dataSet, metricId, fncKey, applyRows)
                    calcCtx[fncKey] = true
                    return dataSet.getRowData(calcCtx.row, fncKey);
                }

            }

            // Evaluate Rows

            for (const row of applyRows) {
                let bValueFound = false;
                for (const cf of caseFunctions) {
                    if(cf.cndFunc){
                        if (cf.cndFunc(row)) {
                            dataSet.setRowData(row, self._storeKey, cf.value);
                            bValueFound = true;
                            break;
                        }
                    } else if(cf.expFunc) {
                        // Create row Scope
                        calcCtx.row = row
                        let scope: any = {}
                        _.forEach(self._requiredMetricsIds, (metricId) => {
                            scope[metricId] = dataSet.getRowMetric(row, metricId)
                        })
                        _.forEach(self._requiredAttributeIds, (attrId) => {
                            scope[attrId] = dataSet.getRowAttribute(row, attrId)
                        })
                        _.defaults(scope, fncScope)
                        let bResults = cf.expFunc.evaluate(scope)
                        if(bResults) {
                            dataSet.setRowData(row, self._storeKey, cf.value);
                            bValueFound = true;
                            break;
                        }
                    }
                }

                if (!bValueFound)
                    dataSet.setRowData(row, self._storeKey, opts.defaultValue);
            }

            return;
        }

        if(bApplyToFilterScope)
            self._setComputedValuesFilteredRows = (async (dataSet: IMetricDataSet,
                                                          rowAttributeIds: string[],
                                                          rowMetricIds: string[],
                                                          filterRows: IdsRow[],
                                                          allRows: IdsRow[]): Promise<void> => {
                await ComputeValues(dataSet, rowAttributeIds, rowMetricIds, filterRows, allRows);
            })
        else
            self._setComputedValuesAllRows = (async (dataSet: IMetricDataSet,
                                                     rowAttributeIds: string[],
                                                     rowMetricIds: string[],
                                                     allRows: IdsRow[]): Promise<void> => {
                await ComputeValues(dataSet, rowAttributeIds, rowMetricIds, [], allRows);
            })



    }

    private setupManualComputedAttribute(): void {
        let opts = this._options;
        this._getComputedValue = opts.getComputedValue;
        if (_.isFunction(opts.setComputedValuesAllRows))
            this._setComputedValuesAllRows = opts.setComputedValuesAllRows;
        else
            this._setComputedValuesAllRows = undefined;
        if (_.isFunction(opts.setComputedValuesFilteredRows))
            this._setComputedValuesFilteredRows = opts.setComputedValuesFilteredRows;
        else
            this._setComputedValuesFilteredRows = undefined;
        this._requiredMetricsIds = opts.requiredMetricsIds || [];
        this._requiredAttributeIds = opts.requiredAttributeIds || [];
    }

    private setupEmptyComputedAttribute(): void {
        this._getComputedValue = () => undefined;
        this._setComputedValuesAllRows = undefined;
        this._setComputedValuesFilteredRows = undefined;
        this._requiredMetricsIds = [];
        this._requiredAttributeIds = [];
    }


}